/**
 * 서비스 안내 투어의 대본.
 *
 * 화면을 어둡게 깔고 기능을 하나씩 밝혀 설명한다. 그리는 일은 {@link TourOverlay} 가 하고,
 * 여기에는 <b>무엇을 어떤 순서로 말하는가</b> 만 둔다 — 문구를 고치러 컴포넌트를 열지 않도록.
 *
 * ## 대상은 `data-tour` 로 가리킨다
 *
 * CSS 클래스나 DOM 구조로 요소를 찾으면 스타일을 손볼 때마다 안내가 조용히 깨진다. 설명할 요소에는
 * `data-tour="..."` 를 직접 달아 두고 여기서 그 이름만 부른다. 이름이 붙은 요소가 화면에 없으면
 * (권한이 없어 안 보이거나, 모바일에서 서랍에 숨어 있거나) 그 걸음은 <b>조용히 건너뛴다</b> —
 * 안내가 없는 기능을 가리키며 멈춰 있는 것이 제일 나쁘다.
 *
 * ## 판번호를 올리면 모두가 한 번 더 본다
 *
 * 기능이 늘어 대본을 고쳤는데 이미 본 사람에게 영영 안 뜨면 안내를 고친 보람이 없다.
 * {@link TOUR_VERSION} 을 올리면 저장된 값(`seenVersion`)보다 커지고, 다음 접속에서 자동으로 한 번 뜬다.
 * 오타 수정처럼 다시 보여줄 것 없는 변경에는 올리지 않는다.
 */

export type TourPlacement = 'auto' | 'top' | 'bottom' | 'left' | 'right';

export type TourStep = {
  key: string;
  /** 하이라이트할 요소의 `data-tour` 값. null 이면 대상 없이 화면 가운데 카드만 띄운다. */
  target: string | null;
  /**
   * 하이라이트 <b>안에서</b> 한 번 더 밝힐 곳. 반짝이는 테두리를 두른다.
   *
   * 밝힌 영역이 넓을 때(사이드바 그룹 하나 전체) "그 안의 어디" 를 말로만 짚으면 눈이 헤맨다.
   * 대상이 없으면 조용히 건너뛴다 — 바깥 하이라이트는 그대로 남는다.
   */
  subTargets?: string[];
  /**
   * 이 걸음 동안 강제로 펼쳐 둘 사이드바 항목(`menuKey`).
   * 투어 중에는 클릭이 막히므로, 접혀 있는 것을 설명하려면 대신 펼쳐 주는 수밖에 없다.
   */
  expandMenuKeys?: string[];
  /**
   * 이 걸음 동안 <b>대신 열어 둘 판</b>(옆판·모달). 값은 그 판을 여는 버튼의 `data-tour` 이름이다.
   *
   * <p>같은 사정이다 — 투어 중에는 클릭이 막혀 사람이 못 열고, 반대로 판이 열려 있으면 <b>? 버튼이
   * 가려져</b> 그 안에서 안내를 시작할 수도 없다. 그래서 걸음이 열어 준다({@code lib/tour/panels}).
   *
   * <p>이 값이 있는 걸음은 <b>대상이 아직 화면에 없어도 걸러 내지 않는다</b> — 열리면 생길 것이기
   * 때문이다. 하이라이트는 요소가 나타나는 순간 따라붙는다.
   */
  openPanelKeys?: string[];
  /**
   * 이 걸음 동안 <b>대신 열어 둘 탭</b>. 값은 그 탭 버튼의 `data-tour` 이름이다
   * (`pmis-tab-progress` · {@code lib/tour/tabs}).
   *
   * <p>탭 줄만 밝히고 「열 개가 목차입니다」 로 끝내면, 정작 <b>탭 안에서 무엇을 누르면 무슨 일이
   * 일어나는지</b> 는 아무도 알려주지 않는다. 투어 중에는 사람이 눌러 볼 수도 없다 — 그래서
   * 걸음이 열어 준다(사용자 지시 2026-08-27).
   *
   * <p>이 값이 있는 걸음은 대상이 <b>지금 화면에 없어도 걸러 내지 않는다</b>(탭을 열면 생긴다).
   * 대신 <b>탭 버튼 자체가 없으면</b> 걸러 낸다 — 권한이 없어 안 보이는 탭을 설명하지 않는다.
   */
  openTabKey?: string;
  /**
   * 이 걸음을 <b>볼 수 있는 권한</b>. 없으면 그 사람의 안내에서 통째로 빠진다.
   *
   * <h2>왜 화면에서 못 거르나</h2>
   * 보통은 「대상이 화면에 없으면 알아서 빠진다」 로 충분하다. 그런데 {@link openPanelKeys} 로 여는
   * 판 <b>안</b>의 요소는 <b>시작할 때 아직 없다</b> — 그래서 그 걸음들은 걸러내기에서 예외로 두고
   * 있고, 그 예외가 «등록 권한이 있어야 보이는 버튼» 까지 함께 살려 버린다. 조회만 하는 사람에게
   * 「여기서 추가하세요」 가 뜨는 모양이다.
   *
   * <p>그래서 <b>권한은 권한으로</b> 거른다. 판정은 {@code AppLayout} 이 세션으로 한다 —
   * 대본은 값(데이터)이라 훅을 부를 수 없고, 부를 수 있어도 그 지식이 여기 있을 이유가 없다.
   *
   * @see resolveMenuCapability
   */
  requires?: { menuKey: string; action: 'view' | 'create' | 'edit' | 'delete' | 'manage' };
  /**
   * <b>직접 해 보는 걸음</b>(사용자 지시 2026-11-02 · 수행관리 튜토리얼).
   *
   * <p>이 걸음 동안은 막이 걷힌다 — 화면이 어두워지지 않고 클릭도 삼키지 않는다. 밝힌 자리에는
   * 테두리만 남고, 사람이 그 버튼을 실제로 누르고 칸에 실제로 적는다. 저장도 진짜다.
   * 어둡게 두지 않는 이유: 그 자리에서 뜨는 모달(`.modal-overlay` 200)이 어둠(1001) 아래에 깔린다.
   */
  interactive?: boolean;
  /**
   * 이 `data-tour` 요소가 화면에 나타나면 <b>다음 걸음으로 저절로</b> 넘어간다.
   * 「[오늘 일보] 를 누르세요」 다음에 [다음] 을 한 번 더 누르게 하지 않으려는 것이다. [다음] 은 그대로 눌린다.
   */
  advanceWhen?: string;
  /**
   * 이 걸음에서 <b>사람이 할 일</b>(사용자 지시 2026-11-02 — 「실제 버튼을 누르면 다음 단계로, 다음을 누르면
   * 그 버튼을 실제로 해 봐 주던」).
   * <ul>
   *   <li>{@code 'click'} — 짚은 버튼을 사람이 누르면 다음으로 넘어가고, [다음] 을 누르면 안내가 그 버튼을
   *       <b>대신 누른다</b>. 버튼이 꺼져 있으면(칸을 안 채운 [저장]) 대신 누르지 못한다고 말하고 넘기기를 고르게 한다.</li>
   *   <li>{@code 'choose'} — 고르는 칸(상태). 사람이 값을 바꾸면 넘어간다. [다음] 은 그냥 넘어간다.</li>
   * </ul>
   * 없으면 설명만 하는 걸음이다 — <b>삭제</b>와 결재 올리기처럼 되돌리기 어려운 것은 대신 누르지 않는다.
   */
  act?: 'click' | 'choose';
  title: string;
  body: string;
  /*
    한때 카드 안에 <b>번호 매긴 절차</b>(`flow`)를 함께 그렸다. 없앴다(사용자 지시 2026-08-26) —
    한 장에 1·2·3·4·5 가 쌓이면 그 카드만 길어지고, 정작 <b>그 절차가 화면 어디서 일어나는지</b>는
    아무 데도 안 밝혀졌다. 지금은 절차 한 칸이 <b>걸음 하나</b>이고, 걸음마다 그 자리를 짚는다
    (`pageGuides` 의 `spots`). 짚을 요소가 없는 칸은 `target: null` 로 카드만 뜬다.
  */
  placement?: TourPlacement;
};

/** 서버에 진행 기록을 남길 때 쓰는 이름. 소문자·숫자·하이픈만 쓴다(서버가 같은 모양을 검증한다). */
export const TOUR_KEY = 'app-intro';

/**
 * 대본 판번호. 올리면 이미 본 사람에게도 다시 한 번 뜬다.
 *
 * 2 — v1 회의 화면들이 사이드바 하위 항목으로 돌아온 것을 알린다(DOC-24).
 *     v1 을 쓰던 사람이 가장 헷갈려 한 자리라, 이미 안내를 본 사람에게도 한 번 더 보여야 한다.
 * 3 — ? 버튼이 화면마다 다른 안내를 연다(DOC-30). 버튼의 뜻이 바뀌었으므로 한 번 더 알린다.
 */
export const TOUR_VERSION = 3;

export const TOUR_STEPS: TourStep[] = [
  {
    key: 'welcome',
    target: null,
    title: 'BoMI Lounge 에 오신 것을 환영합니다',
    body:
      '사내 업무를 한곳에서 처리하는 공간입니다. 어디에 무엇이 있는지 1분 안에 짚어 드릴게요. ' +
      '지금 건너뛰어도 오른쪽 위 물음표 버튼으로 언제든 다시 볼 수 있습니다.',
  },
  {
    key: 'sidebar-menu',
    target: 'sidebar-menu',
    title: '왼쪽이 전체 메뉴입니다',
    body:
      '공지·인사·현장처럼 업무별 화면이 여기 모여 있습니다. 한 줄을 누르면 그 묶음으로 가고, ' +
      '그 안의 화면들은 <b>화면 맨 위에 탭</b>으로 놓입니다. 줄에 마우스를 올리면 나오는 ★ 로 ' +
      '자주 쓰는 메뉴를 맨 위 「즐겨찾기」 칸에 담을 수 있습니다. 보이는 메뉴는 계정 권한에 따라 다릅니다.',
    placement: 'right',
  },
  {
    key: 'sidebar-work',
    target: 'sidebar-work',
    title: '예전 주간회의·월간회의는 「업무 일정」 안에 있습니다',
    body:
      '이 줄을 누르면 업무 일정으로 가고, 예전 그 화면들이 위쪽 탭으로 나옵니다 — ' +
      '주간업무 아래 본사·현장·준공현장, 월간업무 아래 본사·현장 주요일정·현장 회의일정. ' +
      '쓰던 즐겨찾기 주소도 그대로 열립니다.',
    placement: 'right',
  },
  {
    key: 'sidebar-search',
    target: 'sidebar-search',
    title: '메뉴가 많으면 검색하세요',
    body: '메뉴 이름 일부만 쳐도 목적지가 바로 나옵니다. "설정" 처럼 상위 이름으로 치면 그 아래 화면이 함께 나옵니다.',
    placement: 'right',
  },
  {
    key: 'home-widgets',
    target: 'home-widgets',
    title: '첫 화면을 활용하세요',
    body:
      '메뉴마다 필요한 것(오늘 할일, 공지, 결재 대기 등)을 위젯으로 봅니다. ' +
      '위젯 눌러 해당 화면으로 바로 들어갈 수 있습니다.',
  },
  {
    key: 'sidebar-widgets',
    target: 'sidebar-widgets',
    title: '위젯은 직접 커스텀 합니다',
    body: '"위젯 관리" 에서 보일 위젯과 순서·크기를 정합니다. 설정은 계정에 저장되니 다른 기기에서도 그대로 따라옵니다.',
    placement: 'right',
  },
  {
    key: 'header-ai-agent',
    // 헤더 알약에서 오른쪽 아래의 떠 있는 동그라미로 옮겼다(2026-09-17). 카드는 왼쪽에 세운다 —
    // 화면 맨 오른쪽 아래라 아래·오른쪽으로 펴면 카드가 화면 밖으로 나간다.
    target: 'fab-ai-agent',
    title: '찾기 어려우면 AI Agent 에게 물어보세요',
    body: '오른쪽 아래 동그란 버튼입니다. "이번 달 회의 일정 알려줘" 처럼 말로 물으면 사내 자료를 찾아 답합니다. 권한이 없는 자료는 답에 들어가지 않습니다.',
    placement: 'left',
  },
  {
    key: 'header-directory',
    target: 'header-directory',
    title: '사람은 여기서 찾습니다',
    body: '부서·현장별 조직도에서 연락처와 오늘 근무 상태(출장·휴가 등)를 봅니다. 어느 화면에서든 열립니다.',
    placement: 'bottom',
  },
  {
    /*
      메일함 (DOC-108 · 2026-10-06). 안내 카드 한 칸이면서 <b>AI 가 짚을 수 있는 이름</b>이다 —
      이 줄의 `target` 이 곧 대화가 고를 수 있는 자리 이름이 된다({@link collectScreenAnchors}).
      운영 실패 목록의 「메일확인」이 「화면 안내 없음」으로 끝난 자리가 여기였다: 메일은 화면이
      아니라 헤더의 버튼이라 안내할 경로가 없었고, 이름이 없으니 가리킬 수도 없었다.

      ★ `TOUR_VERSION` 은 올리지 않았다 — 올리면 이미 안내를 본 전 직원에게 투어가 처음부터
        다시 뜬다. 버튼 하나가 는 것으로 그러기에는 방해가 크고, 새로 들어온 사람은 이 칸을 본다.
    */
    key: 'header-mail',
    target: 'header-mail',
    title: '메일은 여기서 봅니다',
    body:
      '[메일함] 을 누르면 하이웍스 메일이 새 탭에서 <b>로그인 없이</b> 열립니다. ' +
      '처음 한 번만 하이웍스 비밀번호를 묻고, 그 뒤로는 묻지 않습니다. ' +
      '좁은 화면에서는 오른쪽 위 ☰ 안에 같은 버튼이 있습니다.',
    placement: 'bottom',
  },
  {
    key: 'header-mypage',
    target: 'header-mypage',
    title: '내 정보는 마이페이지에서',
    body: '연차 잔여, 급여명세, 자격증처럼 나에게만 보이는 자료와 비밀번호 변경이 여기 있습니다.',
    placement: 'bottom',
  },
  {
    key: 'header-improvement',
    target: 'header-improvement',
    title: '불편하면 바로 알려 주세요',
    body: '"개선사항 요청" 을 누르면 요청 목록으로 갑니다. 거기서 "등록" 으로 접수하면 담당자가 반영여부와 소요기간을 답합니다.',
    placement: 'bottom',
  },
  {
    key: 'header-help',
    target: 'header-help',
    title: '막히면 그 화면에서 물음표를 누르세요',
    body:
      '이 버튼은 지금 보고 있는 화면에 맞는 설명을 엽니다 — 결재 화면에서 누르면 결재 이야기를, ' +
      '자료실에서 누르면 자료실 이야기를 합니다. 홈에서 누르면 방금 본 이 안내가 처음부터 다시 나옵니다. 이제 시작해 보세요.',
    placement: 'bottom',
  },
];
